import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Download, LockKeyhole, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import connectLogo from '@assets/aitechcare-connect-logo.png';
import { Button } from '@components/common/Button';
import { Brand } from '@components/common/Brand';
import { Container } from '@components/common/Container';
import { EditorialHeader, Overline, PageSection } from '@components/common/Editorial';
import { usePageTitle } from '@hooks/usePageTitle';
import { useRobotsDirective } from '@hooks/useRobotsDirective';
import { downloadsApi } from '@services/downloads';
const downloadCatalog = [
    {
        id: 'connect',
        titleKey: 'downloads.products.connect.title',
        descriptionKey: 'downloads.products.connect.description',
        downloadKey: 'downloads.products.connect.download',
        logo: 'connect',
        logoAltKey: 'downloads.products.connect.logoAlt'
    },
    {
        id: 'update',
        titleKey: 'downloads.products.update.title',
        descriptionKey: 'downloads.products.update.description',
        downloadKey: 'downloads.products.update.download',
        logo: 'corporate',
        logoAltKey: 'downloads.products.update.logoAlt'
    },
    {
        id: 'full_update',
        titleKey: 'downloads.products.fullUpdate.title',
        descriptionKey: 'downloads.products.fullUpdate.description',
        downloadKey: 'downloads.products.fullUpdate.download',
        logo: 'corporate',
        logoAltKey: 'downloads.products.fullUpdate.logoAlt'
    }
];
export const DownloadsPage = () => {
    const { t, i18n } = useTranslation();
    const [phase, setPhase] = useState('checking');
    const [status, setStatus] = useState(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const errorRef = useRef(null);
    usePageTitle('downloads.title');
    useRobotsDirective('noindex,nofollow');
    useEffect(() => {
        let current = true;
        void downloadsApi.status().then((result) => {
            if (!current)
                return;
            setStatus(result);
            setPhase(result.authorized ? 'authorized' : 'locked');
        }).catch(() => current && setPhase('unavailable'));
        return () => { current = false; };
    }, []);
    useEffect(() => { if (error)
        errorRef.current?.focus(); }, [error]);
    const submit = async (event) => {
        event.preventDefault();
        if (busy || !/^\d{6}$/.test(code))
            return;
        setBusy(true);
        setError('');
        try {
            await downloadsApi.access(code);
            const result = await downloadsApi.status();
            if (!result.authorized)
                throw new globalThis.Error('not_authorized');
            setCode('');
            setStatus(result);
            setPhase('authorized');
        }
        catch {
            setCode('');
            setError(t('downloads.invalidCode'));
        }
        finally {
            setBusy(false);
        }
    };
    const lock = async () => {
        setBusy(true);
        try {
            await downloadsApi.logout();
        }
        finally {
            setStatus(null);
            setPhase('locked');
            setBusy(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(EditorialHeader, { eyebrow: t('downloads.eyebrow'), title: t('downloads.title'), intro: t('downloads.intro') }), _jsx(PageSection, { children: _jsxs(DownloadContainer, { children: [phase === 'checking' && _jsx(Status, { role: "status", children: t('downloads.checking') }), phase === 'unavailable' && _jsx(Status, { role: "alert", children: t('downloads.unavailable') }), phase === 'locked' && (_jsxs(AccessCard, { children: [_jsx(LockKeyhole, { size: 30, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx(Overline, { children: t('downloads.restricted') }), _jsx("h2", { children: t('downloads.accessTitle') }), _jsx("p", { children: t('downloads.accessLead') })] }), _jsxs("form", { onSubmit: submit, noValidate: true, children: [_jsx("label", { htmlFor: "download-code", children: t('downloads.codeLabel') }), _jsx(CodeInput, { id: "download-code", name: "code", type: "text", inputMode: "numeric", autoComplete: "one-time-code", pattern: "[0-9]{6}", minLength: 6, maxLength: 6, value: code, onChange: (event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6)), "aria-describedby": error ? 'download-code-error' : undefined, "aria-invalid": Boolean(error), disabled: busy, required: true, autoFocus: true }), error && _jsx(ErrorMessage, { id: "download-code-error", ref: errorRef, role: "alert", tabIndex: -1, children: error }), _jsx(Button, { type: "submit", disabled: busy || code.length !== 6, children: t(busy ? 'downloads.validating' : 'downloads.continue') })] }), _jsxs(Privacy, { children: [_jsx(ShieldCheck, { size: 20, "aria-hidden": "true" }), t('downloads.securityNote')] })] })), phase === 'authorized' && status?.authorized && (_jsxs(AuthorizedArea, { children: [_jsx(ProductGrid, { children: downloadCatalog.map((product) => (_jsx(DownloadProductCard, { product: product, file: status.files.find((candidate) => candidate.id === product.id), locale: i18n.language }, product.id))) }), _jsx(SessionActions, { children: _jsx(SecondaryButton, { type: "button", onClick: () => void lock(), disabled: busy, children: t('downloads.closeSession') }) })] }))] }) })] }));
};
const DownloadProductCard = ({ product, file, locale }) => {
    const { t } = useTranslation();
    const [starting, setStarting] = useState(false);
    const available = file?.available === true;
    return (_jsxs(ProductCard, { children: [_jsx(LogoSlot, { children: product.logo === 'connect' ? (_jsx(ConnectLogo, { src: connectLogo, alt: t(product.logoAltKey) })) : (_jsx(CorporateLogo, { role: "img", "aria-label": t(product.logoAltKey), children: _jsx(Brand, {}) })) }), _jsxs(ProductBody, { children: [_jsxs(ProductIntro, { children: [_jsx(Overline, { children: t(available ? 'downloads.available' : 'downloads.fileUnavailable') }), _jsx("h2", { children: t(product.titleKey) }), _jsx("p", { children: t(product.descriptionKey) })] }), _jsxs(Metadata, { children: [_jsxs("div", { children: [_jsx("dt", { children: t('downloads.fileName') }), _jsx("dd", { className: "file-name", children: file?.fileName ?? t('downloads.unavailableValue') })] }), _jsxs("div", { children: [_jsx("dt", { children: t('downloads.version') }), _jsx("dd", { children: file?.version ?? t('downloads.unavailableValue') })] }), _jsxs("div", { children: [_jsx("dt", { children: t('downloads.size') }), _jsx("dd", { children: typeof file?.sizeBytes === 'number' ? formatBytes(file.sizeBytes, locale) : t('downloads.unavailableValue') })] }), _jsxs("div", { children: [_jsx("dt", { children: "SHA-256" }), _jsx("dd", { children: file?.sha256 ? _jsx("code", { children: file.sha256 }) : t('downloads.unavailableValue') })] })] }), _jsx(CardSpacer, { "aria-hidden": "true" }), available ? (_jsxs(DownloadLink, { href: downloadsApi.fileUrl(product.id), download: true, onClick: () => {
                            setStarting(true);
                            window.setTimeout(() => setStarting(false), 1500);
                        }, "aria-label": t(product.downloadKey), children: [_jsx(Download, { size: 18, "aria-hidden": "true" }), t(starting ? 'downloads.preparing' : product.downloadKey)] })) : (_jsx(UnavailableButton, { type: "button", disabled: true, children: t('downloads.fileUnavailable') }))] })] }));
};
const formatBytes = (bytes, locale) => {
    const [value, unit] = bytes >= 1024 * 1024
        ? [bytes / 1024 / 1024, 'MB']
        : bytes >= 1024
            ? [bytes / 1024, 'KB']
            : [bytes, 'B'];
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${unit}`;
};
const DownloadContainer = styled(Container) `max-width: 1200px;`;
const Status = styled.p `
  padding: 2rem; text-align: center; color: ${({ theme }) => theme.colors.muted};
`;
const AccessCard = styled.section `
  display: grid; grid-template-columns: auto 1fr; gap: 1rem 1.25rem;
  padding: clamp(1.5rem, 5vw, 2.5rem); border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.borderRadius.lg}; background: white;
  box-shadow: ${({ theme }) => theme.shadows.soft};
  > svg { color: ${({ theme }) => theme.colors.blue600}; }
  h2 { margin: 0.45rem 0; font-size: clamp(1.5rem, 4vw, 2rem); }
  p { color: ${({ theme }) => theme.colors.muted}; line-height: 1.65; }
  form { grid-column: 2; display: grid; gap: 0.7rem; }
  label { font-weight: 700; }
  @media (max-width: 36rem) { grid-template-columns: 1fr; form { grid-column: 1; } }
`;
const CodeInput = styled.input `
  min-height: 3.4rem; padding: 0.75rem 1rem; border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.borderRadius.md}; font: 700 1.35rem/1 ${({ theme }) => theme.typography.fontFamily};
  letter-spacing: 0.28em; text-align: center;
  &:focus-visible { outline: 3px solid rgba(95, 176, 201, 0.35); border-color: ${({ theme }) => theme.colors.blue600}; }
`;
const ErrorMessage = styled.p `margin: 0 !important; color: ${({ theme }) => theme.colors.danger} !important;`;
const Privacy = styled.div `
  grid-column: 2; display: flex; align-items: center; gap: 0.55rem; color: ${({ theme }) => theme.colors.muted}; font-size: 0.875rem;
  @media (max-width: 36rem) { grid-column: 1; }
`;
const ProductCard = styled.section `
  display: flex; min-width: 0; height: 100%; overflow: hidden; flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.line}; border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: white; box-shadow: ${({ theme }) => theme.shadows.soft};
`;
const LogoSlot = styled.div `
  display: grid; width: 100%; height: 8.5rem; flex: 0 0 8.5rem; place-items: center;
  padding: 1.25rem 1.5rem 0.75rem;
`;
const ConnectLogo = styled.img `
  display: block; width: min(100%, 15rem); max-height: 4.75rem; object-fit: contain;
`;
const CorporateLogo = styled.div `
  display: inline-flex; align-items: center; justify-content: center; transform: scale(1.18);
`;
const ProductBody = styled.div `
  display: flex; min-width: 0; flex: 1; flex-direction: column; align-items: flex-start;
  padding: 0 clamp(1.25rem, 3vw, 2rem) clamp(1.25rem, 3vw, 2rem);
`;
const ProductIntro = styled.div `
  width: 100%;
  h2 { margin: 0.45rem 0; font-size: clamp(1.45rem, 3vw, 2rem); overflow-wrap: anywhere; }
  p { margin: 0; color: ${({ theme }) => theme.colors.muted}; line-height: 1.7; }
  @media (min-width: 62.01rem) {
    min-height: 17rem;
  }
`;
const Metadata = styled.dl `
  display: grid; width: 100%; gap: 0; margin: 1.5rem 0; padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}; background: ${({ theme }) => theme.colors.surface};
  div { display: grid; min-height: 3.35rem; grid-template-columns: 6.25rem minmax(0, 1fr); gap: 0.75rem; align-items: start; }
  div:last-child { min-height: 5.5rem; }
  dt { color: ${({ theme }) => theme.colors.muted}; font-size: 0.8rem; font-weight: 650; line-height: 1.45; }
  dd { min-width: 0; margin: 0; font-weight: 650; line-height: 1.45; }
  .file-name, code { overflow-wrap: anywhere; word-break: break-word; }
  code { display: block; font-size: 0.8rem; line-height: 1.5; }
  @media (max-width: 42rem) {
    div { min-height: 0; padding: 0.35rem 0; }
    div:last-child { min-height: 0; }
  }
  @media (max-width: 22rem) { div { grid-template-columns: 1fr; gap: 0.15rem; } }
`;
const CardSpacer = styled.span `display: block; flex: 1;`;
const AuthorizedArea = styled.div `display: grid; gap: 1.5rem;`;
const ProductGrid = styled.div `
  display: grid; grid-auto-rows: 1fr; grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(1rem, 2vw, 1.5rem); align-items: stretch;
  @media (max-width: 62rem) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 42rem) { grid-auto-rows: auto; grid-template-columns: minmax(0, 1fr); }
`;
const SessionActions = styled.div `display: flex; justify-content: center;`;
const DownloadLink = styled.a `
  display: inline-flex; width: 100%; min-height: 4.25rem; align-items: center; justify-content: center; gap: 0.55rem;
  padding: 0.75rem 1rem; border-radius: ${({ theme }) => theme.borderRadius.md}; color: white; text-align: center;
  background: ${({ theme }) => theme.colors.blue600}; font-weight: 700; line-height: 1.3; text-decoration: none;
  transition: background 160ms ease, transform 160ms ease;
  &:hover { background: ${({ theme }) => theme.colors.navy900}; }
  &:active { transform: translateY(1px); }
  &:focus-visible { outline: 3px solid rgba(95, 176, 201, 0.4); outline-offset: 3px; }
`;
const UnavailableButton = styled.button `
  width: 100%; min-height: 4.25rem; padding: 0.75rem 1rem; border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.borderRadius.md}; background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.muted}; font: inherit; font-weight: 700; line-height: 1.3; cursor: not-allowed;
`;
const SecondaryButton = styled.button `
  min-height: 3rem; padding: 0.75rem 1rem; border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.borderRadius.md}; background: white; font: inherit; font-weight: 700; cursor: pointer;
`;
